import React from "react";
import {
  fireEvent,
  render as rtlRender,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import { MemoryRouter, Route, Routes, useLocation } from "react-router-dom";
import {
  CanvasViewGridRenderer,
  isSearchableField,
} from "./CanvasViewGridRenderer";
import type { ViewField } from "../../../types";
import type { RenderableElement } from "../CanvasRenderer";
import { CanvasAutoHeightContext } from "../contentHeight";

it("excludes an API-hidden field from canvas grid search", () => {
  const hidden = {
    id: 1,
    is_visible: false,
    column: { data_type: "varchar" },
  } as ViewField;
  expect(isSearchableField(hidden)).toBe(false);
});

const mockUseViewQuery = jest.fn();
const mockUseViewDataQuery = jest.fn();
const mockUseCreateRowMutation = jest.fn();
const mockUseUpdateRowMutation = jest.fn();
const mockUseDeleteRowMutation = jest.fn();
const mockUseCanvasRuntime = jest.fn();
const mockAuthFetch = jest.fn();

jest.mock("../../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));

function render(ui: React.ReactElement) {
  return rtlRender(<MemoryRouter>{ui}</MemoryRouter>);
}

function PathProbe() {
  const location = useLocation();
  return <span data-testid="path">{location.pathname + location.search}</span>;
}

jest.mock("../../../hooks/useViewDataQuery", () => ({
  useViewQuery: (...args: unknown[]) => mockUseViewQuery(...args),
  useViewDataQuery: (...args: unknown[]) => mockUseViewDataQuery(...args),
  useCreateRowMutation: () => mockUseCreateRowMutation(),
  useUpdateRowMutation: () => mockUseUpdateRowMutation(),
  useDeleteRowMutation: () => mockUseDeleteRowMutation(),
}));

jest.mock("../../viewGrid", () => ({
  ViewGridNew: ({
    data,
    onPageChange,
    onRowSelect,
    page,
    allowCreate,
    allowEdit,
    allowDelete,
    onRowCreate,
    onRowUpdate,
    onRowDelete,
    columnDisplay,
    onColumnFilterChange,
    height,
  }: {
    columnDisplay?: unknown;
    height?: number;
    onColumnFilterChange?: (
      filters: Array<{ field: string; operator: string; value?: unknown }>,
    ) => void;
    data: { rows: Array<Record<string, unknown>> };
    onPageChange: (page: number) => void;
    onRowSelect: (id: string, row: Record<string, unknown>) => void;
    page: number;
    allowCreate: boolean;
    allowEdit: boolean;
    allowDelete: boolean;
    onRowCreate?: (data: Record<string, unknown>) => Promise<void>;
    onRowUpdate?: (
      rowId: string,
      data: Record<string, unknown>,
    ) => Promise<void>;
    onRowDelete?: (rowId: string) => Promise<void>;
  }) => (
    <div data-testid="view-grid-new">
      <span data-testid="grid-height">{height}</span>
      <span data-testid="grid-page">{page}</span>
      <span data-testid="grid-column-display">
        {JSON.stringify(columnDisplay ?? null)}
      </span>
      <span data-testid="grid-writes">
        {String(allowCreate)}/{String(allowEdit)}/{String(allowDelete)}
      </span>
      {data.rows.map((row) => (
        <button
          key={String(row._rowId)}
          onClick={() => onRowSelect(String(row._rowId), row)}
        >
          {String(row.name)}
        </button>
      ))}
      <button onClick={() => onPageChange(2)}>Next page</button>
      {onColumnFilterChange && (
        <>
          <button
            onClick={() =>
              onColumnFilterChange([
                {
                  field: "Legal First Name",
                  operator: "contains",
                  value: "Lena",
                },
              ])
            }
          >
            Filter column
          </button>
          <button onClick={() => onColumnFilterChange([])}>
            Clear column filter
          </button>
        </>
      )}
      {onRowCreate && (
        <button onClick={() => void onRowCreate({ name: "New" })}>
          Create row
        </button>
      )}
      {onRowUpdate && (
        <button onClick={() => void onRowUpdate("row-1", { name: "Changed" })}>
          Update row
        </button>
      )}
      {onRowDelete && (
        <button onClick={() => void onRowDelete("row-1")}>Delete row</button>
      )}
    </div>
  ),
  ViewGridSkeleton: () => <div data-testid="view-grid-skeleton">loading</div>,
}));

jest.mock("../CanvasRuntimeContext", () => ({
  useCanvasRuntime: () => mockUseCanvasRuntime(),
}));

const element: RenderableElement = {
  id: "grid-1",
  type: "view_grid",
  props: {
    title: "Projects",
    data_source_alias: "main",
  },
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 12,
  rowSpan: 4,
};

const dataSources = [
  {
    id: "main",
    name: "main",
    type: "view" as const,
    view_id: "view-1",
    config: {},
  },
];

describe("CanvasViewGridRenderer", () => {
  beforeEach(() => {
    mockUseCanvasRuntime.mockReturnValue(null);
    mockAuthFetch.mockReset();
    mockUseCreateRowMutation.mockReturnValue({
      isPending: false,
      mutateAsync: jest.fn(),
    });
    mockUseUpdateRowMutation.mockReturnValue({
      isPending: false,
      mutateAsync: jest.fn(),
    });
    mockUseDeleteRowMutation.mockReturnValue({
      isPending: false,
      mutateAsync: jest.fn(),
    });
  });

  it("shows read-only grid data without create, edit, or delete controls", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    render(
      <CanvasViewGridRenderer
        element={{
          ...element,
          effectiveAccess: "read",
          props: {
            ...element.props,
            allow_create: true,
            allow_edit: true,
            allow_delete: true,
          },
        }}
        dataSources={dataSources}
      />,
    );
    expect(screen.getByText("Ada")).toBeInTheDocument();
    expect(screen.getByTestId("grid-writes")).toHaveTextContent(
      "false/false/false",
    );
  });

  it.each([
    [true, undefined, 264],
    [false, undefined, 1144],
    [true, 400, 400],
  ])(
    "uses natural page height only in an auto-height scope (%s, explicit %s)",
    (autoHeight, height, expected) => {
      mockUseViewQuery.mockReturnValue({
        data: { fields: [] },
        isLoading: false,
        error: null,
      });
      mockUseViewDataQuery.mockReturnValue({
        data: {
          rows: [1, 2, 3].map((id) => ({
            _rowId: String(id),
            name: String(id),
          })),
          columns: [],
          totalCount: 75,
        },
        isLoading: false,
        error: null,
        refetch: jest.fn(),
      });
      render(
        <CanvasAutoHeightContext.Provider value={Boolean(autoHeight)}>
          <CanvasViewGridRenderer
            element={{
              ...element,
              rowSpan: 20,
              props: { ...element.props, height },
            }}
            dataSources={dataSources}
          />
        </CanvasAutoHeightContext.Provider>,
      );
      expect(screen.getByTestId("grid-height")).toHaveTextContent(
        String(expected),
      );
    },
  );

  it("hands the configured status colours to the grid, and nothing when none are configured", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    const { rerender } = render(
      <CanvasViewGridRenderer
        element={{
          ...element,
          props: {
            ...element.props,
            columnStatusColors: { Status: { Approved: "success" } },
          },
        }}
        dataSources={dataSources}
      />,
    );
    expect(
      JSON.parse(screen.getByTestId("grid-column-display").textContent ?? ""),
    ).toEqual({ Status: { statusColors: { Approved: "success" } } });

    rerender(
      <MemoryRouter>
        <CanvasViewGridRenderer element={element} dataSources={dataSources} />
      </MemoryRouter>,
    );
    expect(screen.getByTestId("grid-column-display")).toHaveTextContent("null");
  });

  it("renders the icon row list only when display is list, and keeps row selection", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    const listElement = {
      ...element,
      props: {
        ...element.props,
        display: "list",
        list_config: { primary_field: "name", icon: "Person", chevron: true },
      },
    };
    const { rerender } = render(
      <CanvasViewGridRenderer
        element={listElement}
        dataSources={dataSources}
      />,
    );
    expect(screen.getByTestId("canvas-row-list")).toBeInTheDocument();
    expect(screen.queryByTestId("view-grid-new")).toBeNull();
    expect(screen.getByText("Ada")).toBeInTheDocument();

    rerender(
      <MemoryRouter>
        <CanvasViewGridRenderer element={element} dataSources={dataSources} />
      </MemoryRouter>,
    );
    expect(screen.getByTestId("view-grid-new")).toBeInTheDocument();
    expect(screen.queryByTestId("canvas-row-list")).toBeNull();
  });

  it("advances the list to page 2 with the same view and query scope", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 26,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    render(
      <CanvasViewGridRenderer
        element={{
          ...element,
          props: {
            ...element.props,
            display: "list",
            list_config: { primary_field: "name" },
          },
        }}
        dataSources={dataSources}
      />,
    );
    fireEvent.click(screen.getByRole("button", { name: "Next" }));
    const last = mockUseViewDataQuery.mock.calls.at(-1);
    const first = mockUseViewDataQuery.mock.calls[0];
    expect(first[1]).toMatchObject({ page: 1, pageSize: 25 });
    expect(last[0]).toBe(first[0]);
    expect(last[1]).toMatchObject({
      page: 2,
      pageSize: 25,
      filters: first[1].filters,
    });
  });

  it("passes the configured per-column widths to the grid", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    render(
      <CanvasViewGridRenderer
        element={{
          ...element,
          props: { ...element.props, column_widths: { Name: 260 } },
        }}
        dataSources={dataSources}
      />,
    );
    expect(
      JSON.parse(screen.getByTestId("grid-column-display").textContent ?? ""),
    ).toEqual({ Name: { width: 260 } });
  });

  it("queries the server with a column-menu filter, from page 1, and drops it when cleared", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 108,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    render(
      <CanvasViewGridRenderer
        element={{
          ...element,
          props: {
            ...element.props,
            default_filters: [
              { field: "Status", operator: "eq", value: "Open" },
            ],
          },
        }}
        dataSources={dataSources}
      />,
    );
    fireEvent.click(screen.getByRole("button", { name: "Next page" }));
    // The mock's page 2 is zero-based, so the query asks for page 3.
    expect(mockUseViewDataQuery.mock.calls.at(-1)?.[1].page).toBe(3);

    fireEvent.click(screen.getByRole("button", { name: "Filter column" }));
    const filtered = mockUseViewDataQuery.mock.calls.at(-1)?.[1];
    expect(filtered.page).toBe(1);
    expect(filtered.filters).toEqual([
      { field: "Status", operator: "eq", value: "Open" },
      { field: "Legal First Name", operator: "contains", value: "Lena" },
    ]);

    fireEvent.click(
      screen.getByRole("button", { name: "Clear column filter" }),
    );
    expect(mockUseViewDataQuery.mock.calls.at(-1)?.[1].filters).toEqual([
      { field: "Status", operator: "eq", value: "Open" },
    ]);
  });

  it("keeps legacy grid elements writable when effective access is absent", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    render(
      <CanvasViewGridRenderer
        element={{
          ...element,
          effectiveAccess: undefined,
          props: {
            ...element.props,
            allow_create: true,
            allow_edit: true,
            allow_delete: true,
          },
        }}
        dataSources={dataSources}
      />,
    );
    expect(screen.getByTestId("grid-writes")).toHaveTextContent(
      "true/true/true",
    );
  });

  it("passes its canvas and element IDs on each row write", async () => {
    const create = jest.fn().mockResolvedValue({});
    const update = jest.fn().mockResolvedValue({});
    const remove = jest.fn().mockResolvedValue({});
    mockUseCreateRowMutation.mockReturnValue({
      isPending: false,
      mutateAsync: create,
    });
    mockUseUpdateRowMutation.mockReturnValue({
      isPending: false,
      mutateAsync: update,
    });
    mockUseDeleteRowMutation.mockReturnValue({
      isPending: false,
      mutateAsync: remove,
    });
    mockUseCanvasRuntime.mockReturnValue({
      canvasId: "canvas-1",
      interactionBus: {
        getRuntimeFilters: () => [],
        getSelectedRow: () => null,
      },
    });
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "row-1", name: "Ada" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    render(
      <CanvasViewGridRenderer
        element={{
          ...element,
          props: {
            ...element.props,
            allow_create: true,
            allow_edit: true,
            allow_delete: true,
          },
        }}
        dataSources={dataSources}
      />,
    );
    fireEvent.click(screen.getByRole("button", { name: "Create row" }));
    fireEvent.click(screen.getByRole("button", { name: "Update row" }));
    fireEvent.click(screen.getByRole("button", { name: "Delete row" }));
    const context = { canvasId: "canvas-1", elementId: "grid-1" };
    await waitFor(() =>
      expect(create).toHaveBeenCalledWith({
        viewId: "view-1",
        data: { name: "New" },
        context,
      }),
    );
    expect(update).toHaveBeenCalledWith({
      viewId: "view-1",
      rowId: "row-1",
      data: { name: "Changed" },
      context,
    });
    expect(remove).toHaveBeenCalledWith({
      viewId: "view-1",
      rowId: "row-1",
      context,
    });
  });

  it("groups search with department/status filters, resets paging, and clears excluded selection", () => {
    const dispatchInteraction = jest.fn();
    const selectedRow = {
      rowId: "row-1",
      rowData: {
        _rowId: "row-1",
        name: "Ann",
        department: "Legal",
        status: "active",
      },
    };
    mockUseCanvasRuntime.mockReturnValue({
      canvasId: "canvas-1",
      interactionBus: {
        getRuntimeFilters: () => [],
        getSelectedRow: () => selectedRow,
        dispatchInteraction,
      },
    });
    mockUseViewQuery.mockReturnValue({
      data: {
        fields: [
          {
            id: 1,
            visible: true,
            alias: "name",
            column: { data_type: "varchar" },
          },
        ],
      },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: { rows: [selectedRow.rowData], columns: [], totalCount: 1 },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    const filteredElement = {
      ...element,
      props: {
        ...element.props,
        filter_controls: [
          {
            field: "department",
            label: "Department",
            operator: "eq",
            option_source: "static",
            options: [
              { label: "Legal", value: "Legal" },
              { label: "Sales", value: "Sales" },
            ],
            default_value: "Legal",
          },
          {
            field: "status",
            label: "Status",
            operator: "eq",
            option_source: "static",
            options: [
              { label: "Active", value: "active" },
              { label: "Closed", value: "closed" },
            ],
          },
        ],
      },
    } as RenderableElement;

    render(
      <CanvasViewGridRenderer
        element={filteredElement}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(mockUseViewDataQuery).toHaveBeenCalledWith(
      "view-1",
      expect.objectContaining({ canvasId: "canvas-1" }),
    );
    fireEvent.click(screen.getByText("Next page"));
    expect(screen.getByTestId("grid-page")).toHaveTextContent("2");
    expect(dispatchInteraction).not.toHaveBeenCalled();
    fireEvent.click(screen.getByRole("combobox", { name: "Status" }));
    fireEvent.click(screen.getByRole("option", { name: "Closed" }));
    expect(screen.getByTestId("grid-page")).toHaveTextContent("0");
    expect(dispatchInteraction).toHaveBeenCalledWith(
      expect.objectContaining({ type: "selection_cleared" }),
    );
    fireEvent.change(screen.getByPlaceholderText("Search all records"), {
      target: { value: "Ann" },
    });
    const query = mockUseViewDataQuery.mock.calls.at(-1)?.[1];
    expect(query.filters).toEqual([
      { field: "department", operator: "eq", value: "Legal" },
      { field: "status", operator: "eq", value: "closed" },
    ]);
    expect(query.filterGroups).toEqual([
      {
        logic: "OR",
        filters: [{ field: "1", operator: "contains", value: "Ann" }],
      },
    ]);
    expect(query.page).toBe(1);
    fireEvent.click(screen.getByRole("combobox", { name: "Status" }));
    fireEvent.click(screen.getByRole("option", { name: "All" }));
    expect(mockUseViewDataQuery.mock.calls.at(-1)?.[1].filters).toEqual([
      { field: "department", operator: "eq", value: "Legal" },
    ]);
    dispatchInteraction.mockClear();
    fireEvent.change(screen.getByPlaceholderText("Search all records"), {
      target: { value: "Nobody" },
    });
    expect(dispatchInteraction).toHaveBeenCalledWith(
      expect.objectContaining({ type: "selection_cleared" }),
    );
  });

  it("never removes a fixed author filter when the matching dropdown changes or selects All", () => {
    mockUseCanvasRuntime.mockReturnValue(null);
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "r1", name: "Ann" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    const filteredElement = {
      ...element,
      props: {
        ...element.props,
        default_filters: [
          { field: "department", operator: "eq", value: "Legal" },
        ],
        filter_controls: [
          {
            field: "department",
            label: "Department",
            operator: "eq",
            option_source: "static",
            options: [
              { label: "Legal", value: "Legal" },
              { label: "Sales", value: "Sales" },
            ],
          },
        ],
      },
    } as RenderableElement;
    render(
      <CanvasViewGridRenderer
        element={filteredElement}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );
    expect(mockUseViewDataQuery.mock.calls.at(-1)?.[1].filters).toEqual([
      { field: "department", operator: "eq", value: "Legal" },
    ]);
    fireEvent.click(screen.getByRole("combobox", { name: "Department" }));
    fireEvent.click(screen.getByRole("option", { name: "Sales" }));
    expect(mockUseViewDataQuery.mock.calls.at(-1)?.[1].filters).toEqual([
      { field: "department", operator: "eq", value: "Legal" },
      { field: "department", operator: "eq", value: "Sales" },
    ]);
    fireEvent.click(screen.getByRole("combobox", { name: "Department" }));
    fireEvent.click(screen.getByRole("option", { name: "All" }));
    expect(mockUseViewDataQuery.mock.calls.at(-1)?.[1].filters).toEqual([
      { field: "department", operator: "eq", value: "Legal" },
    ]);
  });

  it("opens a configured canvas from the clicked row", async () => {
    const target = "22222222-2222-4222-8222-222222222222";
    mockUseCanvasRuntime.mockReturnValue({
      workspaceId: "workspace-1",
      canvasId: "source-canvas",
      rowId: null,
      interactionBus: {
        getSelectedRow: jest.fn(),
        dispatchInteraction: jest.fn(),
        getRuntimeFilters: () => [],
      },
    });
    mockAuthFetch.mockResolvedValue({ id: target });
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: {
        rows: [{ _rowId: "selected-row", name: "Selected row" }],
        columns: [],
        totalCount: 1,
      },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });
    rtlRender(
      <MemoryRouter
        initialEntries={[
          "/workspace/workspace-1/myvault/canvases/source-canvas",
        ]}
      >
        <Routes>
          <Route
            path="/workspace/:workspaceId/myvault/canvases/:id"
            element={
              <>
                <CanvasViewGridRenderer
                  element={{
                    ...element,
                    props: {
                      ...element.props,
                      row_navigation: true,
                      row_click_action: {
                        type: "NAVIGATE",
                        config: {
                          target_canvas_id: target,
                          record_source: "selected",
                        },
                      },
                    },
                  }}
                  dataSources={dataSources}
                  canvasId="source-canvas"
                />
                <PathProbe />
              </>
            }
          />
        </Routes>
      </MemoryRouter>,
    );
    fireEvent.click(screen.getByRole("button", { name: "Selected row" }));
    await waitFor(() =>
      expect(screen.getByTestId("path")).toHaveTextContent(
        `/workspace/workspace-1/myvault/canvases/${target}?row=selected-row`,
      ),
    );
  });

  it("shows an explicit empty state when the bound view has no rows", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: { rows: [], columns: [], totalCount: 0 },
      isLoading: false,
      error: null,
      refetch: jest.fn(),
    });

    render(
      <CanvasViewGridRenderer
        element={element}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );

    expect(screen.getByText("No records yet.")).toBeInTheDocument();
    expect(screen.queryByTestId("view-grid-new")).not.toBeInTheDocument();
  });

  it("shows an inline auth state when record access is denied", () => {
    mockUseViewQuery.mockReturnValue({
      data: { fields: [] },
      isLoading: false,
      error: null,
    });
    mockUseViewDataQuery.mockReturnValue({
      data: null,
      isLoading: false,
      error: { status: 403, response: { detail: "Access denied" } },
      refetch: jest.fn(),
    });

    render(
      <CanvasViewGridRenderer
        element={element}
        dataSources={dataSources}
        canvasId="canvas-1"
      />,
    );

    expect(
      screen.getByText("You do not have access to these records."),
    ).toBeInTheDocument();
    expect(screen.queryByTestId("view-grid-new")).not.toBeInTheDocument();
  });
});
